import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import { ApiToken } from '../types';
import FluentIcon from '../components/FluentIcon';
import config from '../config';

interface WhoisResult {
  domain: string;
  success: boolean;
  data?: any;
  error?: string;
  response_time?: number;
  source?: string;
}

const ApiTesting: React.FC = () => {
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [selectedToken, setSelectedToken] = useState<string>('');
  const [domain, setDomain] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [result, setResult] = useState<WhoisResult | null>(null);
  const [testHistory, setTestHistory] = useState<WhoisResult[]>([]);
  const [showAllDomains, setShowAllDomains] = useState<boolean>(false);

  // Sample domains with 100 unique extensions (most popular TLDs)
  const sampleDomains = [
    'google.com', 'wikipedia.org', 'asp.net', 'example.info', 'test.biz', 'sample.name', 'demo.pro', 'hello.mobi',
    'bbc.co.uk', 'sap.de', 'orange.fr', 'baidu.cn', 'sony.jp', 'yandex.ru', 'mit.edu', 'nasa.gov',
    'github.io', 'hello.tech', 'my.app', 'secure.bank', 'news.blog', 'shop.store', 'learn.online', 'fast.delivery',
    'creative.design', 'global.network', 'digital.agency', 'modern.studio', 'smart.solutions', 'contact.tel', 'service.coop', 'local.museum',
    'example.asia', 'travel.aero', 'job.jobs', 'post.post', 'cat.cat', 'xxx.xxx', 'mobile.tel', 'travel.travel',
    'amazon.ca', 'vodafone.au', 'rakuten.jp', 'naver.kr', 'tmall.cn', 'flipkart.in', 'mercadolibre.br', 'olx.pl',
    'bol.nl', 'zalando.de', 'leboncoin.fr', 'avito.ru', 'gumtree.co.za', 'trademe.co.nz', 'finn.no', 'blocket.se',
    'dba.dk', 'marktplaats.nl', 'subito.it', 'willhaben.at', 'ricardo.ch', 'aukro.cz', 'allegro.pl', 'ozon.ru',
    'wildberries.ru', 'lamoda.ru', 'dns-shop.ru', 'mvideo.ru', 'eldorado.ru', 'technopark.ru', 'citilink.ru', 'regard.ru',
    'example.tv', 'sample.cc', 'test.me', 'demo.co', 'hello.ly', 'short.ly', 'bit.ly', 'tiny.cc',
    'example.ws', 'sample.tk', 'test.ml', 'demo.ga', 'hello.cf', 'free.gq', 'cheap.tk', 'gratis.ml',
    'example.us', 'sample.eu', 'test.be', 'demo.at', 'hello.ch', 'site.es', 'web.it', 'page.pt',
    'example.mx', 'sample.ar', 'test.cl', 'demo.pe', 'hello.co', 'site.ve', 'web.uy', 'page.py',
    'example.za', 'sample.ng', 'test.ke', 'demo.gh', 'hello.ma', 'site.eg', 'web.tn', 'page.dz'
  ];

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    try {
      const response = await ApiService.getTokens();
      const activeTokens = response.filter((token: ApiToken) => token.is_active);
      setTokens(activeTokens);
      if (activeTokens.length > 0) {
        // Find default token first, otherwise use first token
        const defaultToken = activeTokens.find((token: ApiToken) => 
          token.name.toLowerCase().includes('default')
        );
        setSelectedToken(defaultToken?.token || activeTokens[0].token || '');
      }
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
    }
  };

  const testWhoisApi = async () => {
    if (!selectedToken || !domain) {
      alert('Please select a token and enter a domain');
      return;
    }

    setLoading(true);
    const startTime = Date.now();

    try {
      const response = await fetch(`${config.whoisApiUrl}/whois/${domain}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${selectedToken}`,
          'Content-Type': 'application/json'
        }
      });

      const responseTime = Date.now() - startTime;
      const data = await response.json();

      const testResult: WhoisResult = {
        domain,
        success: response.ok && data?.success !== false, // Use both HTTP status AND API success field
        data: response.ok && data?.success !== false ? data : undefined,
        error: response.ok && data?.success !== false ? undefined : (data?.error_code ? `${data.error_code}: ${data.detail || 'TLD not supported'}` : typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail || data) || 'Unknown error'),
        response_time: responseTime,
        source: data?.query?.source_used || data?.query?.data_source || 'unknown'
      };

      setResult(testResult);
      setTestHistory(prev => [testResult, ...prev.slice(0, 9)]); // Keep last 10 results
    } catch (error) {
      const responseTime = Date.now() - startTime;
      const testResult: WhoisResult = {
        domain,
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
        response_time: responseTime
      };
      setResult(testResult);
      setTestHistory(prev => [testResult, ...prev.slice(0, 9)]);
    } finally {
      setLoading(false);
    }
  };

  const formatJson = (obj: any) => {
    return JSON.stringify(obj, null, 2);
  };

  const getStatusColor = (success: boolean) => {
    return success ? '#10b981' : '#ef4444';
  };

  const getStatusBg = (success: boolean) => {
    return success ? '#f0fdf4' : '#fef2f2';
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ marginBottom: '16px', color: '#495057', fontSize: '14px', fontWeight: 500 }}>
            API Endpoint
          </div>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: 600, 
            color: '#212529', 
            fontFamily: 'monospace',
            backgroundColor: '#f8f9fa',
            padding: '8px 12px',
            borderRadius: '4px'
          }}>
            GET /api/whois/{'{domain}'}
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ marginBottom: '16px', color: '#495057', fontSize: '14px', fontWeight: 500 }}>
            Active Tokens
          </div>
          <div style={{ 
            fontSize: '28px', 
            fontWeight: 700, 
            color: '#212529', 
            lineHeight: 1.2 
          }}>
            {tokens.length}
          </div>
          <div style={{ color: '#40c057', fontSize: '13px' }}>
            Ready for testing
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ marginBottom: '16px', color: '#495057', fontSize: '14px', fontWeight: 500 }}>
            Test History
          </div>
          <div style={{ 
            fontSize: '28px', 
            fontWeight: 700, 
            color: '#212529', 
            lineHeight: 1.2 
          }}>
            {testHistory.length}
          </div>
          <div style={{ color: '#6c757d', fontSize: '13px' }}>
            {testHistory.filter(t => t.success).length} successful
          </div>
        </div>
      </div>

      {/* Test Form */}
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e9ecef',
        marginBottom: '24px'
      }}>
        <h3 style={{
          margin: '0 0 20px 0',
          fontSize: '18px',
          fontWeight: 600,
          color: '#212529',
          display: 'flex',
          alignItems: 'center'
        }}>
          <FluentIcon name="beaker" size={18} style={{ marginRight: '8px' }} />
          Test WHOIS API
        </h3>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '20px'
        }}>
          {/* API Token Selection */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#495057'
            }}>
              API Token
            </label>
            <select
              value={selectedToken}
              onChange={(e) => setSelectedToken(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #ced4da',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
            >
              {tokens.map((token) => (
                <option key={token.id} value={token.token}>
                  {token.name} ({token.token?.substring(0, 8)}...)
                </option>
              ))}
            </select>
          </div>

          {/* Domain Input */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#495057'
            }}>
              Domain Name
            </label>
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="Enter domain name (e.g., example.com)"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #ced4da',
                borderRadius: '8px',
                fontSize: '14px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
            />
            
            {/* Sample Domains */}
            <div style={{ marginTop: '12px' }}>
              <div style={{ 
                fontSize: '12px', 
                color: '#6c757d', 
                marginBottom: '8px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span>Quick select sample domains ({sampleDomains.length} available):</span>
                <button
                  onClick={() => setShowAllDomains(!showAllDomains)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#667eea',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0f0ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {showAllDomains ? 'Show Less' : 'Show All'}
                </button>
              </div>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '6px',
                maxHeight: showAllDomains ? 'none' : '120px',
                overflow: 'hidden',
                transition: 'max-height 0.3s ease'
              }}>
                {(showAllDomains ? sampleDomains : sampleDomains.slice(0, 24)).map((sampleDomain) => (
                  <button
                    key={sampleDomain}
                    onClick={() => setDomain(sampleDomain)}
                    style={{
                      padding: '4px 10px',
                      fontSize: '12px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      color: '#495057',
                      transition: 'all 0.2s',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                      e.currentTarget.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.borderColor = 'transparent';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                      e.currentTarget.style.background = '#f8f9fa';
                      e.currentTarget.style.color = '#495057';
                      e.currentTarget.style.borderColor = '#e9ecef';
                    }}
                  >
                    {sampleDomain}
                  </button>
                ))}
              </div>
              {!showAllDomains && sampleDomains.length > 24 && (
                <div style={{
                  textAlign: 'center',
                  marginTop: '8px',
                  fontSize: '12px',
                  color: '#6c757d'
                }}>
                  Showing 24 of {sampleDomains.length} domains
                </div>
              )}
            </div>
          </div>

          {/* Test Button */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
          <button
            onClick={testWhoisApi}
            disabled={loading || !selectedToken || !domain}
            style={{
              background: loading ? '#6c757d' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
                padding: '14px 32px',
                borderRadius: '8px',
                fontSize: '16px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
                minWidth: '140px',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }
            }}
          >
            {loading ? 'Testing...' : 'Test API'}
          </button>
          </div>
        </div>
      </div>

      {/* Current Result */}
      {result && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e9ecef',
          marginBottom: '24px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '16px 24px',
            color: 'white'
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span>
                <FluentIcon name="beaker" size={18} style={{ marginRight: '8px' }} />
                Test Result: {result.domain}
              </span>
              <span style={{
                fontSize: '12px',
                backgroundColor: getStatusBg(result.success),
                color: getStatusColor(result.success),
                padding: '4px 8px',
                borderRadius: '4px',
                fontWeight: 600
              }}>
                {result.success ? 'SUCCESS' : 'FAILED'}
              </span>
            </h3>
          </div>

          <div style={{ padding: '24px' }}>
            {/* Result Metadata */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '16px',
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px'
            }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Domain</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#212529' }}>{result.domain}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Response Time</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#212529' }}>{result.response_time}ms</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Source</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#212529' }}>{result.source || 'N/A'}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Status</div>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: 600,
                  color: getStatusColor(result.success)
                }}>
                  {result.success ? 'Success' : 'Error'}
                </div>
              </div>
            </div>

            {/* Result Data */}
            {result.success && result.data ? (
              <div>
                {/* Structured Data View */}
                {result.data.query && (
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: 600, color: '#495057' }}>
                      Key Information:
                    </h4>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                      gap: '16px',
                      marginBottom: '20px'
                    }}>
                      {/* Query Info */}
                      <div style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '6px',
                        border: '1px solid #e9ecef'
                      }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px', fontWeight: 600 }}>
                          Query Information
                        </div>
                        <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                          <div><strong>Domain:</strong> {result.data.query?.domain || 'N/A'}</div>
                          <div><strong>Extension:</strong> {result.data.query?.extension || 'N/A'}</div>
                          <div><strong>Data Source:</strong> {result.data.query?.data_source || 'N/A'}</div>
                          <div><strong>Timestamp:</strong> {result.data.query?.timestamp ? new Date(result.data.query.timestamp).toLocaleString() : 'N/A'}</div>
                        </div>
                      </div>

                      {/* Status Info */}
                      <div style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '6px',
                        border: '1px solid #e9ecef'
                      }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px', fontWeight: 600 }}>
                          Registration Status
                        </div>
                        <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                          <div><strong>Registered:</strong> {result.data.status?.registered ? '✅ Yes' : '❌ No'}</div>
                          <div><strong>Available:</strong> {result.data.status?.available ? '✅ Yes' : '❌ No'}</div>
                          {result.data.status?.reserved && <div><strong>Reserved:</strong> ⚠️ Yes</div>}
                          {result.data.status?.unknown && <div><strong>Unknown:</strong> ❓ Yes</div>}
                        </div>
                      </div>

                      {/* Registration Info */}
                      {result.data.registration && (
                        <div style={{
                          backgroundColor: '#f8f9fa',
                          padding: '16px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px', fontWeight: 600 }}>
                            Registration Details
                          </div>
                          <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                            {result.data.registration.registrar && <div><strong>Registrar:</strong> {result.data.registration.registrar}</div>}
                            {result.data.registration.creation_date && <div><strong>Created:</strong> {new Date(result.data.registration.creation_date).toLocaleDateString()}</div>}
                            {result.data.registration.expiration_date && <div><strong>Expires:</strong> {new Date(result.data.registration.expiration_date).toLocaleDateString()}</div>}
                            {result.data.registration.age_days && <div><strong>Age:</strong> {result.data.registration.age_days} days</div>}
                            {result.data.registration.remaining_days && <div><strong>Days Remaining:</strong> {result.data.registration.remaining_days}</div>}
                          </div>
                        </div>
                      )}

                      {/* Technical Info */}
                      {result.data.technical && (
                        <div style={{
                          backgroundColor: '#f8f9fa',
                          padding: '16px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px', fontWeight: 600 }}>
                            Technical Details
                          </div>
                          <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                            {result.data.technical.name_servers && result.data.technical.name_servers.length > 0 && (
                              <div><strong>Name Servers:</strong> {result.data.technical.name_servers.length} servers</div>
                            )}
                            {result.data.technical.status && result.data.technical.status.length > 0 && (
                              <div><strong>Status:</strong> {result.data.technical.status.join(', ')}</div>
                            )}
                            {result.data.technical.dnssec && <div><strong>DNSSEC:</strong> {result.data.technical.dnssec}</div>}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#495057' }}>
                  Complete JSON Response:
                </h4>
                <pre style={{
                  backgroundColor: '#f8f9fa',
                  padding: '16px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  lineHeight: '1.4',
                  overflow: 'auto',
                  maxHeight: '400px',
                  border: '1px solid #e9ecef',
                  color: '#212529'
                }}>
                  {formatJson(result.data)}
                </pre>
              </div>
            ) : result.error && (
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#dc3545' }}>
                  Error:
                </h4>
                <div style={{
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  border: '1px solid #f5c6cb'
                }}>
                  {typeof result.error === 'string' ? result.error : JSON.stringify(result.error, null, 2)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test History */}
      {testHistory.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '16px 24px',
            color: 'white'
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center'
            }}>
              <span style={{ marginRight: '8px' }}>📝</span>
              Test History ({testHistory.length})
            </h3>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
            gap: '16px',
            padding: '16px 24px',
            backgroundColor: '#f8f9fa',
            fontSize: '12px',
            fontWeight: 600,
            color: '#495057',
            borderBottom: '1px solid #e9ecef'
          }}>
            <div>DOMAIN</div>
            <div>STATUS</div>
            <div>RESPONSE TIME</div>
            <div>SOURCE</div>
            <div>RESULT</div>
          </div>

          {testHistory.map((test, index) => (
            <div
              key={index}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                gap: '16px',
                padding: '12px 24px',
                borderBottom: index < testHistory.length - 1 ? '1px solid #f3f4f6' : 'none',
                alignItems: 'center',
                fontSize: '14px'
              }}
            >
              <div style={{ fontWeight: 600, color: '#212529' }}>{test.domain}</div>
              <div>
                <span style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: getStatusColor(test.success),
                  backgroundColor: getStatusBg(test.success),
                  padding: '2px 6px',
                  borderRadius: '4px'
                }}>
                  {test.success ? 'SUCCESS' : 'FAILED'}
                </span>
              </div>
              <div style={{ color: '#495057' }}>{test.response_time}ms</div>
              <div style={{ color: '#495057' }}>{test.source || 'N/A'}</div>
              <div style={{ 
                color: getStatusColor(test.success),
                fontSize: '12px'
              }}>
                {test.success ? <FluentIcon name="checkmark-circle" size={16} /> : <FluentIcon name="dismiss" size={16} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApiTesting; 